/**
 * useSubscription
 *
 * Fetches the current user's Stripe subscription record from Supabase.
 * Also provides a helper to initiate a Checkout session or open the
 * Customer Portal via the respective Edge Functions.
 *
 * Subscription is required for: strata_admin, property_manager
 * NOT required for:             host, system_admin (guests have no account)
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  price_id: string | null;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  created_at: string;
  updated_at: string;
}

/** Roles that must have an active subscription to access the app */
export const PAID_ROLES = ["strata_admin", "property_manager", "individual"] as const;

/** A subscription is considered active when Stripe reports one of these statuses */
const ACTIVE_STATUSES = new Set(["active", "trialing"]);

// ─── Main hook ────────────────────────────────────────────────────────────────

export function useSubscription() {
  const { user, session } = useAuth();

  const requiresSubscription =
    !!user && (PAID_ROLES as readonly string[]).includes(user.role);

  const query = useQuery<Subscription | null>({
    queryKey: ["subscription", user?.id],
    // Only query for roles that actually need a subscription (strata_admin, property_manager).
    // Hosts and system_admin do not have subscription records — querying for them
    // causes a 404 from PostgREST due to RLS denying table access.
    enabled: !!user?.id && requiresSubscription,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // No retries: a subscription check should fail fast. With retry > 0 the
    // exponential backoff keeps `isLoading` true for many seconds, which
    // freezes the ProtectedRoute loading spinner.
    retry: 0,
    queryFn: async () => {
      if (!user?.id) return null;

      // A user may have multiple rows (e.g. an orphan "incomplete" row with
      // stripe_subscription_id = null from a partial signup, plus a live row
      // from a completed checkout). Using plain .maybeSingle() on an un-filtered
      // result causes PGRST116 ("more than one row") → silently returns null
      // even when a valid subscription exists.
      //
      // Fix: mirror the same safe query used in sync-subscription Edge Function:
      //   • Exclude rows with no Stripe subscription ID (orphan/incomplete)
      //   • Order newest-updated first so the active row comes first
      //   • Limit to 1 so maybeSingle() is always safe
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .not("stripe_subscription_id", "is", null)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // Table not yet created — treat as no subscription.
        if (error.code === "42P01") return null;
        // Any other DB / network error: log a warning and return null so the
        // loading spinner resolves immediately. The user will be redirected to
        // /pricing where they can subscribe (or retry). This is preferable to
        // an infinite loading state caused by a slow/unavailable Supabase
        // connection.
        console.warn("[useSubscription] Could not fetch subscription:", error.message, error.code);
        return null;
      }
      return data as Subscription | null;
    },
  });

  const isActive = requiresSubscription
    ? ACTIVE_STATUSES.has(query.data?.status ?? "")
    : true; // hosts / system_admin are always "active" — no subscription needed

  return {
    subscription: query.data ?? null,
    isActive,
    requiresSubscription,
    // Treat query errors as "not loading" — errors resolve to null (see queryFn),
    // so isError should never be set, but this is a belt-and-suspenders guard.
    isLoading: query.isLoading && !query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ─── Create Checkout Session ──────────────────────────────────────────────────

interface CreateCheckoutParams {
  priceId: string;
  userId: string;
  role: string;
  /** Number of buildings. Only used for property_manager tiered pricing. Defaults to 1. */
  quantity?: number;
}

/**
 * Calls the create-checkout-session Edge Function and redirects
 * the browser to the Stripe-hosted Checkout page.
 */
export function useCreateCheckoutSession() {
  // Use the session already in AuthContext — avoids an extra supabase.auth.getSession()
  // round-trip inside the mutationFn, which can hang when Supabase is slow and keeps
  // the button stuck in "Redirecting to Stripe…" state indefinitely.
  const { session } = useAuth();

  return useMutation<string, Error, CreateCheckoutParams>({
    mutationFn: async ({ priceId, userId, role, quantity }) => {
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated — please sign in again");

      // Abort the Edge Function call after 15 s to prevent infinite "Redirecting to
      // Stripe…" spinner when the Supabase Edge Functions service is unreachable.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15_000);

      try {
        // supabase.functions.invoke automatically attaches the auth header from
        // the client's stored session, so no need to pass it manually.
        const res = await supabase.functions.invoke("create-checkout-session", {
          body: { priceId, userId, role, quantity: quantity ?? 1 },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        // Prefer the specific error from the function body over the generic
        // "Edge Function returned a non-2xx status code" wrapper message.
        if (res.error) {
          const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
          throw new Error(detail);
        }
        const { url, error } = res.data as { url?: string; error?: string };
        if (error) throw new Error(error);
        if (!url) throw new Error("No checkout URL returned");
        return url;
      } catch (err) {
        clearTimeout(timeoutId);
        if ((err as Error).name === "AbortError") {
          throw new Error("Request timed out — please check your connection and try again");
        }
        throw err;
      }
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
  });
}

// ─── Cancel Subscription Immediately ─────────────────────────────────────────

export interface CancelImmediateResult {
  success: boolean;
  refundAmount: number;
  refundAmountDisplay: string | null;
  currency: string;
  /** Describes why a refund was (or wasn't) issued. */
  refundType: "full" | "prorated" | "trial" | "none";
  message: string;
}

/**
 * Calls cancel-subscription-immediate Edge Function, which cancels the Stripe
 * subscription right now with proration and issues a cash refund for unused days.
 */
export function useCancelSubscriptionImmediate() {
  const queryClient = useQueryClient();

  return useMutation<CancelImmediateResult, Error, { subscriptionId: string; userId: string }>({
    mutationFn: async ({ subscriptionId, userId }) => {
      const res = await supabase.functions.invoke("cancel-subscription-immediate", {
        body: { subscriptionId, userId },
      });
      // Prefer the specific error from the function body over the generic wrapper.
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const data = res.data as CancelImmediateResult & { error?: string };
      if (data.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate subscription cache so billing page re-reads the canceled status
      queryClient.invalidateQueries({ queryKey: ["subscription", variables.userId] });
    },
  });
}

// ─── Create Portal Session ────────────────────────────────────────────────────

/**
 * Calls the create-portal-session Edge Function and redirects
 * the browser to the Stripe Customer Portal.
 */
export function useCreatePortalSession() {
  return useMutation<string, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const res = await supabase.functions.invoke("create-portal-session", {
        body: { userId },
      });

      // supabase.functions.invoke puts a generic "Edge Function returned a
      // non-2xx status code" in res.error.message when the function returns
      // an error status, while the actual error body (e.g. the specific Stripe
      // error) is in res.data.error. Prefer the specific message so the toast
      // shown to the user is actionable (e.g. "No default configuration exists
      // for this account's customer portal" → user knows to configure it in
      // the Stripe Dashboard).
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const { url, error } = res.data as { url?: string; error?: string };
      if (error) throw new Error(error);
      if (!url) throw new Error("No portal URL returned");
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
  });
}

// ─── Get Billing Details ──────────────────────────────────────────────────────

export interface StripeInvoice {
  id: string;
  number: string | null;
  status: string | null;
  amountPaid: number;
  amountDue: number;
  currency: string;
  created: number; // unix timestamp
  periodStart: number;
  periodEnd: number;
  invoicePdf: string | null;
  hostedUrl: string | null;
}

export interface StripePaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface StripeUpcomingInvoice {
  amountDue: number;
  currency: string;
  nextPaymentAttempt: number | null;
  periodEnd: number;
}

export interface BillingDetails {
  invoices: StripeInvoice[];
  paymentMethod: StripePaymentMethod | null;
  upcoming: StripeUpcomingInvoice | null;
}

/**
 * Calls get-billing-details Edge Function to get invoices + payment method
 * from Stripe. Only enabled when the user has an active subscription.
 */
export function useGetBillingDetails(userId: string | undefined, enabled: boolean) {
  return useQuery<BillingDetails, Error>({
    queryKey: ["billing-details", userId],
    enabled: !!userId && enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
    queryFn: async () => {
      const res = await supabase.functions.invoke("get-billing-details", {
        body: { userId },
      });
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const data = res.data as BillingDetails & { error?: string };
      if (data.error) throw new Error(data.error);
      return data;
    },
  });
}

// ─── Public Checkout (no auth) ───────────────────────────────────────────────

/**
 * Calls create-public-checkout without auth and redirects to Stripe.
 * Used for the "pay first, sign up after" flow on the pricing page.
 */
export function usePublicCheckout() {
  return useMutation<void, Error, { priceId: string; plan: string; quantity?: number }>({
    mutationFn: async ({ priceId, plan, quantity = 1 }) => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      // Supabase gateway requires Authorization: Bearer even for public (anon) calls
      const res = await fetch(`${supabaseUrl}/functions/v1/create-public-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ priceId, plan, quantity }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? "Checkout failed");
      if (data.url) window.location.href = data.url;
    },
  });
}

// ─── Claim Checkout Session ───────────────────────────────────────────────────

/**
 * Links a completed public checkout session to the current user's account.
 * Called automatically on /pricing when ?checkout_session param is present.
 */
export function useClaimCheckoutSession() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { checkoutSessionId: string }>({
    mutationFn: async ({ checkoutSessionId }) => {
      const res = await supabase.functions.invoke("claim-checkout-session", {
        body: { checkoutSessionId },
      });
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const data = res.data as { success?: boolean; error?: string };
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["subscription", user.id] });
      }
    },
  });
}

// ─── Update Payment Method ────────────────────────────────────────────────────

export interface UpdatePaymentMethodResult {
  success: boolean;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/**
 * After a SetupIntent is confirmed client-side, call this to set the new
 * payment method as default on the subscription.
 */
export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation<
    UpdatePaymentMethodResult,
    Error,
    { userId: string; paymentMethodId: string }
  >({
    mutationFn: async ({ userId, paymentMethodId }) => {
      const res = await supabase.functions.invoke("update-payment-method", {
        body: { userId, paymentMethodId },
      });
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const data = res.data as UpdatePaymentMethodResult & { error?: string };
      if (data.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate billing details so Overview tab refreshes with new card
      queryClient.invalidateQueries({ queryKey: ["billing-details", variables.userId] });
    },
  });
}
